import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PASSWORD_MIN_LENGTH } from "@salesboard/contracts";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { useT, type Translator } from "@/i18n";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { ApiClientError, api } from "@/lib/api";

type TokenState = "valid" | "expired" | "revoked" | "redeemed" | "user_deactivated" | "unknown";
type Preflight = { state: TokenState; email?: string; name?: string };

/**
 * Where an invitation or a reset link lands.
 *
 * Two deliberate mechanics, both about the link surviving to the human:
 *
 * 1. **The token is in the URL fragment**, not the query string. Fragments are never sent
 *    to a server, so the token stays out of access logs, proxy request lines and `Referer`
 *    headers.
 * 2. **The token is not redeemed on load.** Mail-security scanners — Defender Safe Links
 *    and its peers — pre-fetch every URL in an email, so a link that verifies when
 *    fetched is burned before the recipient clicks it. The form renders first, a *POST*
 *    preflight then reports whether the link is usable, and only submitting consumes it.
 *    Scanners do not execute JavaScript, so neither step can be triggered by one.
 */
export function SetPasswordPage(): React.JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [token, setToken] = useState<string>();
  const [preflight, setPreflight] = useState<Preflight>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  useDocumentTitle(t("setPassword.submit"));

  useEffect(() => {
    // Read from the fragment, then strip it from the address bar so the token does not
    // linger in browser history or get copied out of a shared screen.
    const raw = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
    setToken(raw ?? undefined);
    if (raw) window.history.replaceState({}, "", window.location.pathname);

    if (!raw) {
      setPreflight({ state: "unknown" });
      return;
    }

    void api
      .post<Preflight>("/auth/token-preflight", { token: raw })
      .then(setPreflight)
      .catch(() => setPreflight({ state: "unknown" }));
  }, []);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);

    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(t("setPassword.tooShort", PASSWORD_MIN_LENGTH));
      return;
    }
    if (password !== confirm) {
      setError(t("setPassword.mismatch"));
      return;
    }
    if (!token) {
      setError(t("setPassword.missingToken"));
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/set-password", { token, password });
      // No session is minted here deliberately — signing in with the new credential is the
      // proof it works, and auto-login would make this endpoint a session oracle.
      void navigate("/login?passwordSet=1", { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : t("setPassword.failed"));
      setSubmitting(false);
    }
  };

  const dead = preflight && preflight.state !== "valid";

  return (
    <main className="brand-bloom-page flex min-h-dvh w-full items-center justify-center p-4 sm:p-8">
      <div className="bg-card ring-border/60 w-full max-w-sm space-y-6 rounded-xl p-6 shadow-2xl ring-1 sm:p-8">
        <header className="space-y-3">
          <div className="flex items-center gap-3">
            <img
              src="/infa-partner-logo.png"
              alt={t("app.logoAlt")}
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 rounded-sm object-contain"
            />
            <h1 className="text-2xl font-semibold tracking-tight">{t("app.name")}</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {dead ? t("setPassword.deadLead") : t("setPassword.lead")}
          </p>
        </header>

        {dead && (
          <div className="border-destructive/40 bg-destructive/10 space-y-3 rounded-md border p-4">
            <p className="text-sm">{deadMessage(t, preflight.state)}</p>
            <div className="flex flex-wrap gap-2">
              {preflight.state === "redeemed" || preflight.state === "expired" ? (
                <Link to="/forgot-password" className="text-primary text-sm underline">
                  {t("setPassword.requestNew")}
                </Link>
              ) : null}
              <Link to="/login" className="text-muted-foreground text-sm underline">
                {t("setPassword.backToSignIn")}
              </Link>
            </div>
          </div>
        )}

        {!dead && (
          <form onSubmit={submit} className="space-y-4" noValidate>
            {preflight?.email && (
              <p className="text-muted-foreground text-sm">
                {t("setPassword.settingFor")} <span className="text-foreground font-medium">{preflight.email}</span>
              </p>
            )}

            <Field
              label={t("setPassword.newPassword")}
              htmlFor="password"
              hint={t("setPassword.hint", PASSWORD_MIN_LENGTH)}
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <Field label={t("setPassword.confirm")} htmlFor="confirm">
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </Field>

            {error && (
              <p role="alert" className="text-destructive text-sm">
                {error}
              </p>
            )}

            <Button type="submit" disabled={submitting || !preflight} className="w-full">
              {submitting ? t("common.saving") : t("setPassword.submit")}
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}

function deadMessage(t: Translator, state: TokenState): string {
  switch (state) {
    case "expired":
      return t("setPassword.expired");
    case "revoked":
      return t("setPassword.revoked");
    case "redeemed":
      return t("setPassword.redeemed");
    case "user_deactivated":
      return t("setPassword.deactivated");
    default:
      return t("setPassword.invalid");
  }
}
